import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { FormikSelect } from '../../../common/formik-material-ui';
import { ModalWithStyles } from './ModalWithStyles';
import { MenuItem, CircularProgress } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { user, IUserAtom } from '../../../recoil/atoms/user';
import { useRecoilStateLoadable } from 'recoil';
import { useSnackbarWithStyles } from '../notifications/SnackbarProviderWithStyles';
import { LabelledOutline } from '../../../common/final-form-material-ui';
// import { endpointEnum } from '../../../services/rest/makeAPIRequest';
import { useMutation } from 'react-query';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import { toAbsoluteUrl } from '../../../_metronic/_helpers';
import { extractErrorMessage } from 'src/utils/errorHandling/extractErrorMessage';
import { useFeatureFlag } from 'src/app/hooks/useFeatureFlag';

const useStyles = makeStyles((theme) => ({
  sectionTitle: {
    fontWeight: 700,
    margin: '4px 8px',
  },
  sectionValue: {
    margin: '4px 8px',
  },
  sectionContainer: {
    // display: 'flex',
    // justifyContent: 'flex-start'
  },
  numberDetailsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '32px',
  },
  labelledOutline: {
    '& legend': {
      width: '102px',
    },
    margin: '16px 0px',
  },
  loadingIcon: {
    background: '#fff',
    borderRadius: '50%',
    // margin: '-80px auto 0',
    alignSelf: 'center',
    width: 150,
    height: 150,
    padding: 25,
    textAlign: 'center',
    margin: '24px auto',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
  },
  bulletText: {
    fontWeight: 500,
  },
  bulletTextContainer: {
    margin: '24px 0px',
    listStyleType: 'circle',
  },
}));

export function ReplaceNumberModal({
  show,
  handleClose,
  fromPhoneNumber,
  number,
}) {
  const classes = useStyles();
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [replacementNumber, setReplacementNumber] = useState<any>(null);
  const [userAtomLoadable, setUserAtom] = useRecoilStateLoadable(user);
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const userAtomContents = userAtomLoadable.contents as IUserAtom;
  const mutation = useMutation(getApiMutation(userAtomContents, setUserAtom));
  const { treatment: treatmentReplaceNumber } = useFeatureFlag(
    'SWARM-46-replace-number-failed'
  );
  const isReplaceNumberFixingEnabled =
    treatmentReplaceNumber === 'control' || treatmentReplaceNumber === 'on';

  const deleteOldNumberAfter = [
    {
      label: 'Never',
      value: 'never',
    },
    {
      label: '1 week',
      value: '1',
    },
    {
      label: '2 weeks',
      value: '2',
    },
    {
      label: '3 weeks',
      value: '3',
    },
  ];

  const getLogo = () => {
    return toAbsoluteUrl('/media/logos/replace-number-success.png');
  };

  useEffect(() => {
    if (replacementNumber) {
      setTabIndex(2);
    } else {
      setTabIndex(0);
    }
  }, [replacementNumber]);

  useEffect(() => {
    setTabIndex(0);
  }, [fromPhoneNumber]);

  return (
    <Formik
      onSubmit={async (values, actions) => {
        switch (tabIndex) {
          case 0:
            setTabIndex(1);
            try {
              const response = await mutation.mutateAsync({
                endpoint: 'legacy/replaceNumber',
                payload: {
                  userid: userAtomContents?.userid,
                  deleteAfter: values.deleteAfter,
                  call: 'replaceNumber',
                  number:
                    values.number?.[0] === '+'
                      ? values.number
                      : '+' + values.number,
                },
              });

              if (!response?.data?.success) {
                const defaultMessage =
                  'An error occurred replacing the number. Please try again later.'
                let errorMessage = '';
                if (isReplaceNumberFixingEnabled) {
                  errorMessage = extractErrorMessage(response, {
                    context: 'ReplaceNumberModal',
                    defaultMessage,
                  });
                } else {
                  errorMessage = response?.data?.error ?? defaultMessage;
                }

                enqueueSnackbarWithStyles({
                  message: errorMessage,
                  alertProps: {
                    severity: 'error',
                  },
                });
                setTabIndex(0);
                handleClose();
              } else {
                if (response?.data?.data?.[0]) {
                  setReplacementNumber(response.data.data[0]);
                }
              }
            } catch (e) {
              enqueueSnackbarWithStyles({
                message:
                  'An error occurred replacing the number. Please try again later.',
                alertProps: {
                  severity: 'error',
                },
              });
              setTabIndex(0);
              handleClose();
            }
            break;
          case 1:
            handleClose();
            break;
          case 2:
            handleClose();
            break;
          default:
            break;
        }
        actions.resetForm({});
      }}
      enableReinitialize={true}
      initialValues={{
        number: fromPhoneNumber,
        numberName: '',
        deleteAfter: 'never',
      }}
    >
      {(formik) => (
        <ModalWithStyles
          show={show}
          onSubmit={formik.handleSubmit}
          title="Replace Number"
          aria-labelledby="replace-number-modal"
          onHide={() => {
            formik.resetForm({});
            handleClose();
          }}
          submitLabel={tabIndex ? 'Done' : 'Proceed'}
          disableSubmit={!formik.isValid}
          isSubmitting={tabIndex === 1}
          size={tabIndex === 0 ? 'sm' : 'md'}
        >
          {tabIndex === 0 ? (
            <>
              <div style={{ margin: '16px 0px' }}>
                Replacing this number will search for and purchase a new number
                that will be added to your Kixie Numbers. The new number will be
                added to your Kixie bill as an additional charge. Click Proceed
                to continue or Cancel to go back.
                <br />
                <br />
                Additionally, you can choose to either keep your old number on
                your account or remove it after a given period of time. Keeping
                your old number helps inbound callers connect to your agents.
              </div>
              {/* <br /> */}
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <FormikSelect
                  name="deleteAfter"
                  // className={classes.field}
                  formik={formik}
                  style={{ width: '240px' }}
                  // fullWidth
                  label="Delete old number after"
                  required
                  size="small"
                  variant="outlined"
                >
                  {deleteOldNumberAfter.map((option, index) => (
                    <MenuItem key={index} value={option.value}>
                      {option.label}
                    </MenuItem>
                  )) ?? []}
                </FormikSelect>
              </div>
              <br />
            </>
          ) : tabIndex === 1 ? (
            <div className="row">
              <div className={'col-lg-7'}>
                <LabelledOutline
                  className={classes.labelledOutline}
                  label="Replacing number"
                >
                  <div>
                    <div
                      className={clsx(classes.numberDetailsContainer, 'row')}
                    >
                      <div
                        className={clsx(classes.sectionContainer, 'col-lg-4')}
                      >
                        <div className={classes.sectionTitle}>Type</div>
                        <div className={classes.sectionValue}>
                          {number?.type ? number.type : '-'}
                        </div>
                      </div>
                      <div
                        className={clsx(classes.sectionContainer, 'col-lg-4')}
                      >
                        <div className={classes.sectionTitle}>Phone Number</div>
                        <div className={classes.sectionValue}>
                          {number?.DID ? number.DID : '-'}
                        </div>
                      </div>
                      <div
                        className={clsx(classes.sectionContainer, 'col-lg-4')}
                      >
                        <div className={classes.sectionTitle}>Name</div>
                        <div className={classes.sectionValue}>
                          {number?.numberName ? number.numberName : '-'}
                        </div>
                      </div>
                    </div>
                    <div
                      className={clsx(classes.numberDetailsContainer, 'row')}
                    >
                      <div
                        className={clsx(classes.sectionContainer, 'col-lg-4')}
                      >
                        <div className={classes.sectionTitle}>Destination</div>
                        <div className={classes.sectionValue}>
                          {number?.destinationName
                            ? number.destinationName
                            : '-'}
                        </div>
                      </div>
                      <div
                        className={clsx(classes.sectionContainer, 'col-lg-4')}
                      >
                        <div className={classes.sectionTitle}>Default User</div>
                        <div className={classes.sectionValue}>
                          {number?.defaultuserName
                            ? number.defaultuserName
                            : '-'}
                        </div>
                      </div>
                      <div
                        className={clsx(classes.sectionContainer, 'col-lg-4')}
                      >
                        <div className={classes.sectionTitle}>Status</div>
                        <div className={classes.sectionValue}>
                          {number?.portStatus ? number.portStatus : '-'}
                        </div>
                      </div>
                    </div>
                    <div
                      className={clsx(classes.numberDetailsContainer, 'row')}
                    >
                      <div
                        className={clsx(classes.sectionContainer, 'col-lg-4')}
                      >
                        <div className={classes.sectionTitle}>
                          T-Mobile Status
                        </div>
                        <div className={classes.sectionValue}>
                          {number?.tmobileImageData
                            ? number.tmobileImageData
                            : '-'}
                        </div>
                      </div>
                      <div
                        className={clsx(classes.sectionContainer, 'col-lg-4')}
                      >
                        <div className={classes.sectionTitle}>ATT Status</div>
                        <div className={classes.sectionValue}>
                          {number?.attImageData ? number.attImageData : '-'}
                        </div>
                      </div>
                      <div
                        className={clsx(classes.sectionContainer, 'col-lg-4')}
                      >
                        <div className={classes.sectionTitle}>
                          Verizon Status
                        </div>
                        <div className={classes.sectionValue}>
                          {number?.verizonImageData
                            ? number.verizonImageData
                            : '-'}
                        </div>
                      </div>
                    </div>
                    <div
                      className={clsx(classes.numberDetailsContainer, 'row')}
                    >
                      <div
                        className={clsx(classes.sectionContainer, 'col-lg-4')}
                      >
                        <div className={classes.sectionTitle}>Spam Likely</div>
                        <div className={classes.sectionValue}>
                          {number?.ScamLikelyManual ? 'Yes' : 'No'}
                        </div>
                      </div>
                    </div>
                  </div>
                </LabelledOutline>
              </div>
              <div className={'col-lg-5'}>
                <div className={classes.loadingIcon}>
                  <CircularProgress
                    size={100}
                    style={{ verticalAlign: 'center' }}
                  />
                </div>
                <div>
                  <ul>
                    <li className={classes.bulletTextContainer}>
                      <div className={classes.bulletText}>
                        Searching for a new number
                      </div>
                    </li>
                    <li className={classes.bulletTextContainer}>
                      <div className={classes.bulletText}>
                        Purchasing new number
                      </div>
                    </li>
                    <li className={classes.bulletTextContainer}>
                      <div className={classes.bulletText}>
                        Removing old number associations
                      </div>
                    </li>
                    <li className={classes.bulletTextContainer}>
                      <div className={classes.bulletText}>
                        Applying new number associations
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ alignItems: 'center' }} className="row">
              <div className={'col-lg-6'}>
                <div>
                  <div className={clsx(classes.numberDetailsContainer, 'row')}>
                    <div className={clsx(classes.sectionContainer, 'col-lg-4')}>
                      <div className={classes.sectionTitle}>Type</div>
                      <div className={classes.sectionValue}>
                        {replacementNumber?.type ? replacementNumber.type : '-'}
                      </div>
                    </div>
                    <div className={clsx(classes.sectionContainer, 'col-lg-4')}>
                      <div className={classes.sectionTitle}>Phone Number</div>
                      <div className={classes.sectionValue}>
                        {replacementNumber?.DID ? replacementNumber.DID : '-'}
                      </div>
                    </div>
                    <div className={clsx(classes.sectionContainer, 'col-lg-4')}>
                      <div className={classes.sectionTitle}>Name</div>
                      <div className={classes.sectionValue}>
                        {replacementNumber?.numberName
                          ? replacementNumber.numberName
                          : '-'}
                      </div>
                    </div>
                  </div>
                  <div className={clsx(classes.numberDetailsContainer, 'row')}>
                    <div className={clsx(classes.sectionContainer, 'col-lg-4')}>
                      <div className={classes.sectionTitle}>Destination</div>
                      <div className={classes.sectionValue}>
                        {replacementNumber?.destinationName
                          ? replacementNumber.destinationName
                          : '-'}
                      </div>
                    </div>
                    <div className={clsx(classes.sectionContainer, 'col-lg-4')}>
                      <div className={classes.sectionTitle}>Default User</div>
                      <div className={classes.sectionValue}>
                        {replacementNumber?.defaultuser
                          ? number?.defaultuserName
                          : '-'}
                      </div>
                    </div>
                    <div className={clsx(classes.sectionContainer, 'col-lg-4')}>
                      <div className={classes.sectionTitle}>Status</div>
                      <div className={classes.sectionValue}>
                        {replacementNumber?.portStatus
                          ? replacementNumber.portStatus
                          : '-'}
                      </div>
                    </div>
                  </div>
                  <div className={clsx(classes.numberDetailsContainer, 'row')}>
                    <div className={clsx(classes.sectionContainer, 'col-lg-4')}>
                      <div className={classes.sectionTitle}>
                        T-Mobile Status
                      </div>
                      <div className={classes.sectionValue}>
                        {replacementNumber?.tmobileImageData
                          ? replacementNumber.tmobileImageData
                          : '-'}
                      </div>
                    </div>
                    <div className={clsx(classes.sectionContainer, 'col-lg-4')}>
                      <div className={classes.sectionTitle}>ATT Status</div>
                      <div className={classes.sectionValue}>
                        {replacementNumber?.attImageData
                          ? replacementNumber.attImageData
                          : '-'}
                      </div>
                    </div>
                    <div className={clsx(classes.sectionContainer, 'col-lg-4')}>
                      <div className={classes.sectionTitle}>Verizon Status</div>
                      <div className={classes.sectionValue}>
                        {replacementNumber?.verizonImageData
                          ? replacementNumber.verizonImageData
                          : '-'}
                      </div>
                    </div>
                  </div>
                  <div className={clsx(classes.numberDetailsContainer, 'row')}>
                    <div className={clsx(classes.sectionContainer, 'col-lg-4')}>
                      <div className={classes.sectionTitle}>Spam Likely</div>
                      <div className={classes.sectionValue}>
                        {replacementNumber?.ScamLikelyManual ? 'Yes' : 'No'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className={'col-lg-6'}>
                <img style={{ height: '400px' }} alt="logo" src={getLogo()} />
                <br />
              </div>
            </div>
          )}
        </ModalWithStyles>
      )}
    </Formik>
  );
}
